/**
 * The navigation document, and the four questions a client has to answer about
 * one (#511, #1466 B3).
 *
 * `core.navigation()` hands back `{ groups: [{ label, items: [{ label, to }] }] }`
 * and nothing else — no provenance, no relationship to whatever the client would
 * have drawn on its own. That is deliberate on the server's side (the resolver
 * answers what the bar IS), and it leaves three things for the client — and then
 * a fourth, added by #1466 B3, which is the one the other three made possible to
 * ask: WHICH RUNG of the cascade any of this came from (see THE LADDER, below).
 * The first three are about one document; the fourth is about the stack of them:
 *
 *  1. **What renders?** Hidden entries are carried IN the document rather than
 *     removed from it, so re-opening the editor can still show you what you
 *     switched off. `visibleNav` is the render filter.
 *
 *  2. **What happens when the deployment changes under a pin?** A pin is a
 *     SNAPSHOT of a bar. Install a module a month later and a naively-stored
 *     snapshot hides it forever, with no way for the user to know there is
 *     anything to un-hide — the exact failure that makes people distrust
 *     personalisation and never touch it again. Because a hidden entry is
 *     written down, "absent from the pin" is unambiguous: it is NEW. It gets
 *     appended. `reconcile` is that rule.
 *
 *     Applied to a USER PIN only. A persona or deployment default renders
 *     exactly as authored: an administrator curating what a whole role sees has
 *     stated a list, and silently growing it is the opposite of the control they
 *     were given. `clear_navigation` is how that one goes back to following the
 *     client.
 *
 *  3. **What may an edit do?** Reorder, hide, and decide the PARTITION — which
 *     section a link sits in, including sections the person invented (#1466 B2,
 *     owner ruling M6 in comment 47222). What it may still never do is conjure a
 *     route or rename a section the deployment declared.
 *
 *     The refusal used to be one joint constraint over the pair (group label,
 *     route), because `inBase` was `Map<groupLabel, Map<route, itemLabel>>`.
 *     That collapsed three invariants into one and only two of them were ever
 *     argued anywhere:
 *
 *       base is the authority on EXISTENCE     — KEPT, on the EDIT path. `rowsFor`
 *         drops any route the deployment does not have, wherever the stored
 *         document put it, so no EDIT can conjure a surface you have no
 *         business on. Said precisely because the review caught the wider claim:
 *         the RENDER path does not re-check — `Nav.tsx:253` never calls
 *         `rowsFor` and `reconcile` passes pin items through verbatim — so this
 *         is a property of editing, not a boundary. The boundary is the router
 *         and the door behind it, as it always was.
 *       base is the authority on ITEM LABELS   — KEPT, and for the same reason:
 *         a nav item that says one thing and goes somewhere else is #331 with a
 *         personalisation feature attached.
 *       base is the authority on the PARTITION — RELAXED. It was never argued;
 *         it was a side effect of keying `inBase` by group label. Historical
 *         id-less authored groups are upgraded to stable identity on their next
 *         save rather than being mistaken for another host's base group.
 *
 *     So a person may move a link into any section, invent a section and name
 *     it, and rename or delete a section THEY created. A BASE section in the
 *     current editor keeps the platform's word for itself. A valid old document
 *     may already carry authored id-less outcome groups, however; discarding
 *     those on first edit would be a destructive migration, so valid routes in
 *     such a group are retained and the group receives stable identity.
 *
 *     Still enforced by construction rather than by validation — `documentFrom`
 *     can only emit routes that came out of `rowsFor`, which can only take them
 *     from the base document. There is no server-side shape check to fall back
 *     on: the SQL doors validate only `jsonb_typeof(document->'groups') = 'array'`,
 *     and `core.validate_record` has never existed.
 */

import { moved as movedByIndex } from '@solum/experience'

export interface NavItem {
  label: string
  to: string
  /**
   * Governed presentation identity for this destination (#2286). The route is
   * still navigation/admission authority; `pageKey` only selects the resolved
   * PageDefinition after that route has been admitted. Absent keeps the
   * host-derived legacy key (`portal.<slug>` / the host's route mapping).
   */
  pageKey?: string
  /** Shared semantic icon key; presentation only, never route authority. */
  iconKey?: string
  /** Carried, not deleted — see (1) above. */
  hidden?: boolean
  /**
   * `soon` — a destination announced but not yet navigable (#2502 §5.1, Crew
   * contracts v1). Drawn with a neutral **Soon** pill, `aria-disabled`, never a
   * link and never focusable; never admitted as a location. Absent = an
   * ordinary destination. A closed set of one, so an unknown value is ignored
   * rather than guessed at.
   */
  state?: 'soon'
  /**
   * Presentation intent carried to the page (#2502, Crew contracts v1.1):
   * "Share feedback" is `{ to: 'feedback', params: { new: '1' } }`. Never a
   * reserved deep-link key (`page`, `help`, `tour`, `article`) — the core
   * trigger refuses those, and the host's location helper drops them again.
   */
  params?: Readonly<Record<string, string>>
}

/** The closed categorical tone set a space may take (`global.css` `--cat-*`). */
export const NAV_GROUP_TONES = ['cat-1', 'cat-2', 'cat-3', 'cat-4', 'cat-5', 'cat-6', 'cat-7', 'cat-8'] as const
export type NavGroupTone = (typeof NAV_GROUP_TONES)[number]
export function isNavGroupTone(v: unknown): v is NavGroupTone {
  return typeof v === 'string' && (NAV_GROUP_TONES as readonly string[]).includes(v)
}

export interface NavGroup {
  /**
   * Absent = a section the BASE declared, identified by `label`. Present = a
   * section a person created (`usr_<8 hex>`), whose identity survives a rename.
   *
   * Optional on purpose (owner ruling M1): every navigation document stored in
   * the fleet today is already valid under this type, nothing needs a backfill,
   * and the SQL doors need no change. Two user sections may legitimately share a
   * name — the id is the only thing that keeps them two.
   */
  id?: string
  label: string
  items: NavItem[]
  hidden?: boolean
  /*
   * A SPACE (#2502 §5.1, Crew contracts v1) — all optional, all presentation:
   * the space key (`elevate`, `support`…) the Timeline and Home scope by, the
   * section glyph (a kit icon name, drawn by `EntityIcon`), and the
   * categorical tone (`cat-1`…`cat-8`). A document that states none is exactly
   * as valid as before.
   */
  key?: string
  glyph?: string
  tone?: NavGroupTone
}
export interface NavDocument {
  groups: NavGroup[]
}

/** The host-owned page registry returned by `portal.navigation(key)`.
 *
 * Kept deliberately smaller than the portal application's wire type: the
 * navigation editor needs only the published page identity, translated label
 * and explicit navigation order.  A console administrator and the portal
 * shell must derive the same base from those facts or the administrator can
 * save a document the portal itself could never have produced.
 */
export interface PortalNavigationRegistry {
  pages?: { slug: string; title?: Record<string, string>; pageKey?: string }[]
  /**
   * `route_only` is the THIRD state a page can be in, and it is the deployment's
   * word rather than the person's (#2329 ruling 3).
   *
   *   listed      in `nav`                      drawn      openable
   *   ROUTED      in `nav`, `route_only: true`  NOT drawn  openable
   *   staged      not in `nav`                  not drawn  not openable
   *
   * Before it existed the three collapsed into one bit: the host filtered its
   * page list to what `nav` listed, built the bar from that list, and admitted a
   * requested slug only if the bar still offered it. So "take it out of the
   * navigation" and "delete the route" were the same edit — which is why
   * `portal/2026082414` could only retire a page by deleting the page object,
   * and why a detail page reached from a work item had nowhere to live.
   *
   * Deliberately NOT `NavItem.hidden`. That one means *"the person switched it
   * off, carried rather than deleted so they can switch it back"* — a fact about
   * one viewer, editable by them. This is a fact about the deployment's
   * information architecture, and sharing a field would make an architectural
   * ruling something any one person could un-hide.
   */
  nav?: { page: string; order?: number; route_only?: boolean }[]
}

/** The slugs a registry keeps REACHABLE without drawing them — the routed set
 *  above. A host admits these alongside whatever its bar offers, so a deep link,
 *  a redirect and a work item can still open them.
 *
 *  Separate from `portalNavigationBase` on purpose: the bar and the admission
 *  set are two different questions, and a host that asks only the first one is
 *  the state this exists to leave behind. */
export function routeOnlySlugs(
  registry: PortalNavigationRegistry | null | undefined,
): Set<string> {
  return new Set((registry?.nav ?? []).filter((e) => e.route_only === true).map((e) => e.page))
}

/** Turn one explicit portal instance's registry into the shared navigation
 * base. Pages that exist but are not in `nav` are staged, not published, and
 * therefore are not editable destinations; pages whose entry is `route_only`
 * are published and reachable but are not navigation siblings, so they are not
 * drawn and not editable destinations either. */
export function portalNavigationBase(
  registry: PortalNavigationRegistry | null | undefined,
  labelOf: (page: NonNullable<PortalNavigationRegistry['pages']>[number]) => string,
): NavDocument {
  const order = new Map((registry?.nav ?? []).map((entry) => [entry.page, entry.order ?? 0]))
  const routed = routeOnlySlugs(registry)
  const pages = [...(registry?.pages ?? [])]
    .filter((page) => order.has(page.slug) && !routed.has(page.slug))
    .sort((a, b) => (order.get(a.slug) ?? 0) - (order.get(b.slug) ?? 0)
      || a.slug.localeCompare(b.slug))
  return {
    groups: [{
      label: 'Pages',
      items: pages.map((page) => ({
        label: labelOf(page), to: page.slug,
        ...(page.pageKey ? { pageKey: page.pageKey } : {}),
      })),
    }],
  }
}

/**
 * A fresh identity for a section a person just created.
 *
 * Not exported from the package barrel: `inventory.test.ts` pins three exact
 * counts over `@solum/ui`'s exports, and this is an internal detail of the
 * editor rather than a control-kit affordance.
 */
export function newSectionId(): string {
  const c = globalThis.crypto
  const hex = c && 'randomUUID' in c
    ? c.randomUUID().replace(/-/g, '')
    : Math.random().toString(16).slice(2).padEnd(8, '0')
  return `usr_${hex.slice(0, 8)}`
}

/**
 * Is this actually a navigation document?
 *
 * Every one of these values arrives from a `jsonb`-returning RPC, so "not null"
 * is not the same question as "is a bar". The portal e2e caught it: its stub
 * answers unmatched reads with `[]`, `core.my_navigation_pin()` got `[]` back,
 * a truthiness check read that as "this user has pinned a bar", and the portal
 * rendered "· customised" with a reset button over a document that was not one.
 * A real deployment reaches the same state from a half-written row or a
 * PostgREST shape nobody expected.
 *
 * One predicate, used for BOTH the resolver answer and the pin, so the two
 * cannot disagree about what counts as a bar.
 */
export function isNavDocument(x: unknown): x is NavDocument {
  return Boolean(x) && typeof x === 'object' && !Array.isArray(x)
    && Array.isArray((x as NavDocument).groups)
}

/** A document a client can render: no hidden entries, no groups left empty. */
export function visibleNav(doc: NavDocument | null | undefined): NavDocument {
  if (!doc || !Array.isArray(doc.groups)) return { groups: [] }
  return {
    groups: doc.groups
      .filter((g) => g && !g.hidden)
      // `id` is carried through, because two user sections may share a NAME and
      // a host that keys its rendered headings by label would collapse them into
      // one React key. Nothing here uses it; it is here so a host can.
      .map((g) => ({
        ...(g.id ? { id: g.id } : {}),
        label: g.label,
        /* The space presentation travels, like `id`: dropping it here would
           strip every space's glyph and tone from a persona's bar. */
        ...(typeof g.key === 'string' ? { key: g.key } : {}),
        ...(typeof g.glyph === 'string' ? { glyph: g.glyph } : {}),
        ...(isNavGroupTone(g.tone) ? { tone: g.tone } : {}),
        items: (g.items ?? []).filter((i) => i && !i.hidden),
      }))
      // A group whose every item is hidden is a heading with nothing under it.
      .filter((g) => g.items.length > 0),
  }
}

/**
 * A stored pin, brought back into line with what the client would draw today.
 *
 * Anything the pin does not MENTION is new since it was written and is appended;
 * anything it mentions as hidden stays hidden. Group order follows the pin, and
 * a group the pin never heard of goes to the end.
 *
 * "Mentioned" is decided by ROUTE, across the whole document, not per group.
 * Keying it per group looked right and duplicated every link whose group had
 * been renamed — which is the portal's ordinary case, where the built-in group
 * is called `Pages` and a persona document calls it something a customer reads.
 * A route that appears anywhere in the pin has been decided about; only a route
 * that appears nowhere is new. That property is also what made sections cheap:
 * a link dragged into an invented section is still "decided about", so it is not
 * re-appended to the base section it came from.
 *
 * A USER SECTION is never a landing place for new routes, and both halves of
 * that matter. A section with an `id` gets no `fresh` items, and it does not
 * count as having covered a base group even when it happens to share its name —
 * otherwise inventing a section called "Billing" would both swallow the next
 * module's links AND make the real Billing section unreachable in the editor.
 */
export function reconcile(pin: NavDocument | null | undefined, base: NavDocument): NavDocument {
  if (!pin || !Array.isArray(pin.groups)) return base
  const seen = new Set<string>()
  const seenGroups = new Set<string>()
  for (const g of pin.groups) {
    if (!g.id) seenGroups.add(g.label)
    for (const i of g.items ?? []) seen.add(i.to)
  }

  /**
   * The BASE is the authority on a route's icon, so a stored item takes it from
   * there rather than from what happened to be written down.
   *
   * `rowsFor`/`documentFrom` were repaired so a reorder no longer drops
   * `iconKey` on the way OUT (ruling 50100 row 2). That fixed every document
   * written since — and did nothing for one written before, because this
   * function passed stored items through by reference and the renderer guards
   * on presence (`{it.iconKey && <EntityIcon …>}`). So anybody who had already
   * reordered their sidebar kept an icon-less bar permanently, and the only
   * ways out were Reset — which discards their arrangement — or opening the
   * editor and saving again, which nothing tells them to do.
   *
   * Re-joining on READ heals those documents in place, and it is the same rule
   * the write path already follows rather than a second one: the label is
   * likewise re-read from the base a few lines below in `rowsFor`. A route the
   * base no longer offers keeps whatever it stored, so a link to a withdrawn
   * module does not lose its icon on the way to being removed.
   */
  const baseIcon = new Map<string, string>()
  const basePage = new Map<string, string>()
  for (const b of base.groups) {
    for (const i of b.items) {
      if (i.iconKey) baseIcon.set(i.to, i.iconKey)
      if (i.pageKey) basePage.set(i.to, i.pageKey)
    }
  }
  const rejoin = (i: NavItem): NavItem => {
    const icon = baseIcon.get(i.to)
    const pageKey = basePage.get(i.to)
    if (icon === i.iconKey && pageKey === i.pageKey) return i
    return {
      ...i,
      ...(icon ? { iconKey: icon } : {}),
      ...(pageKey ? { pageKey } : {}),
    }
  }

  const groups: NavGroup[] = pin.groups.map((g) => {
    const fresh = g.id ? [] : (base.groups.find((b) => b.label === g.label)?.items ?? [])
      .filter((i) => !seen.has(i.to))
    return {
      ...(g.id ? { id: g.id } : {}),
      label: g.label,
      hidden: g.hidden,
      items: [...(g.items ?? []).map(rejoin), ...fresh],
    }
  })
  for (const b of base.groups) {
    if (seenGroups.has(b.label)) continue
    const fresh = b.items.filter((i) => !seen.has(i.to))
    if (fresh.length) groups.push({ ...b, items: fresh })
  }
  return { groups }
}

/** One editable row: a group, or an item inside one. Order is the array order. */
export interface NavEditGroup {
  /** Present = created by a person; renameable and deletable by them. See NavGroup. */
  id?: string
  label: string
  hidden: boolean
  /** Derived repair bucket. It is never a serializable navigation section. */
  recovery?: true
  /**
   * `iconKey` is carried for the same reason `label` is: the BASE is the
   * authority on it, and an editor that drops it writes a document without it.
   *
   * MEASURED (ruling 50100 row 2, "a faithful preview of the resulting
   * navigation"): the console's built-in bar gives every generic entity link an
   * `iconKey` from `buildNav`, `rowsFor` dropped it and `documentFrom` could not
   * emit what it had never been given — so the first time anybody reordered
   * their sidebar, every entity icon in it disappeared and did not come back
   * until they reset. Nothing said so, because a pinned bar is drawn from the
   * pin and the pin genuinely had no icons in it.
   */
  items: { label: string; to: string; hidden: boolean; iconKey?: string; pageKey?: string }[]
}

export const UNPARENTED_SECTION = 'Unparented sections'

export interface ExcludedNavigationItem {
  to: string
  reason: 'unavailable' | 'duplicate'
}

export interface NavigationEditModel {
  rows: NavEditGroup[]
  unparented: NavEditGroup['items']
  excluded: ExcludedNavigationItem[]
}

/**
 * Build the editable document without silently granting or losing a route.
 *
 * A governed route beneath a structurally unusable parent is retained in one
 * derived repair bucket. Unknown/withdrawn routes and later mentions of a route
 * are excluded instead: neither is a destination the editor may manufacture.
 * Historical id-less authored groups remain valid for backward compatibility.
 */
export function navigationEditModel(
  base: NavDocument, value: NavDocument | null | undefined,
): NavigationEditModel {
  if (!value || !Array.isArray(value.groups)) {
    return { rows: rowsFor(base, value), unparented: [], excluded: [] }
  }

  const governed = new Map<string, NavItem>()
  for (const group of base.groups) {
    for (const item of group.items ?? []) if (!governed.has(item.to)) governed.set(item.to, item)
  }

  const seenGroupIds = new Set<string>()
  const seenRoutes = new Set<string>()
  const validGroups: NavGroup[] = []
  const unparented: NavEditGroup['items'] = []
  const excluded: ExcludedNavigationItem[] = []

  for (const group of value.groups) {
    if (!group || typeof group !== 'object') continue
    const duplicateId = Boolean(group.id && seenGroupIds.has(group.id))
    if (group.id) seenGroupIds.add(group.id)
    const invalidParent = typeof group.label !== 'string' || group.label.trim() === '' || duplicateId
    const validItems: NavItem[] = []

    for (const item of Array.isArray(group.items) ? group.items : []) {
      if (!item || typeof item.to !== 'string') continue
      if (seenRoutes.has(item.to)) {
        excluded.push({ to: item.to, reason: 'duplicate' })
        continue
      }
      seenRoutes.add(item.to)
      const source = governed.get(item.to)
      if (!source) {
        excluded.push({ to: item.to, reason: 'unavailable' })
        continue
      }
      const normalized = {
        label: source.label, to: source.to, hidden: Boolean(item.hidden),
        ...(source.iconKey ? { iconKey: source.iconKey } : {}),
        ...(source.pageKey ? { pageKey: source.pageKey } : {}),
      }
      if (invalidParent) unparented.push(normalized)
      else validItems.push({ ...normalized, ...(normalized.hidden ? { hidden: true } : {}) })
    }
    if (!invalidParent) validGroups.push({
      ...(group.id ? { id: group.id } : {}), label: group.label,
      ...(group.hidden ? { hidden: true } : {}), items: validItems,
    })
  }

  const recoveryRoutes = new Set(unparented.map((item) => item.to))
  const rows = rowsFor(base, { groups: validGroups })
    .map((group) => ({ ...group, items: group.items.filter((item) => !recoveryRoutes.has(item.to)) }))
  if (unparented.length) rows.push({
    label: UNPARENTED_SECTION, hidden: false, recovery: true, items: unparented,
  })
  return { rows, unparented, excluded }
}

/**
 * The editor's working state: the BASE bar, ordered, flagged and PARTITIONED by
 * `value`.
 *
 * Base is the authority on what exists and on what each link is called. `value`
 * supplies order, hidden flags and section membership, so a stale document can
 * never make an entry disappear from the editor itself and can never make one
 * appear that the deployment does not have.
 *
 * Six steps, and the last two are what keep the relaxation safe:
 *
 *   1. reconcile, so a module installed since the document was written is in it;
 *   2. index the base FLAT — `route → item label` — rather than per group;
 *   3. keep a group when it has an `id`, the base has its label, or it is a
 *      legacy id-less authored group containing at least one valid base route;
 *   4. keep an item when the base has that route, first mention wins;
 *   5. re-add every base section the document dropped or renamed away, so the
 *      platform's own word for a section always survives a round trip;
 *   6. re-home every base route the document did not place, into the section
 *      that declared it. Routes retained in legacy authored groups remain
 *      placed there and receive a stable custom-group identity.
 */
export function rowsFor(base: NavDocument, value: NavDocument | null | undefined): NavEditGroup[] {
  const merged = reconcile(value, base)

  const routeLabel = new Map<string, string>()
  const routeIcon = new Map<string, string>()
  const routePage = new Map<string, string>()
  const declaredIn = new Map<string, string>()
  for (const g of base.groups) {
    for (const i of g.items ?? []) {
      if (routeLabel.has(i.to)) continue
      routeLabel.set(i.to, i.label)
      if (i.iconKey) routeIcon.set(i.to, i.iconKey)
      if (i.pageKey) routePage.set(i.to, i.pageKey)
      declaredIn.set(i.to, g.label)
    }
  }
  const baseLabels = new Set(base.groups.map((g) => g.label))

  // First mention wins. A stored document that lists one route twice is not an
  // invented link, but it is two rows with the same React key and two places to
  // hide the same thing from — so the second mention is dropped rather than
  // rendered.
  const placed = new Set<string>()
  const rows: NavEditGroup[] = []
  const usedIds = new Set(merged.groups.flatMap((group) => group.id ? [group.id] : []))
  const legacyId = (group: NavGroup, position: number): string => {
    // FNV-1a: deterministic rather than random so a legacy document has stable
    // working identity even before its first explicit save. Position is part of
    // the input because old documents may contain two equally named sections.
    const source = `${position}|${group.label}|${(group.items ?? []).map((item) => item.to).join('|')}`
    let hash = 0x811c9dc5
    for (let i = 0; i < source.length; i += 1) {
      hash ^= source.charCodeAt(i)
      hash = Math.imul(hash, 0x01000193)
    }
    let candidate = `usr_${(hash >>> 0).toString(16).padStart(8, '0')}`
    while (usedIds.has(candidate)) candidate = `usr_${((hash += 1) >>> 0).toString(16).padStart(8, '0')}`
    usedIds.add(candidate)
    return candidate
  }
  for (const [groupPosition, g] of merged.groups.entries()) {
    const items: NavEditGroup['items'] = []
    for (const i of g.items ?? []) {
      if (!routeLabel.has(i.to) || placed.has(i.to)) continue
      placed.add(i.to)
      items.push({
        label: routeLabel.get(i.to)!, to: i.to, hidden: Boolean(i.hidden),
        ...(routeIcon.has(i.to) ? { iconKey: routeIcon.get(i.to)! } : {}),
        ...(routePage.has(i.to) ? { pageKey: routePage.get(i.to)! } : {}),
      })
    }
    const id = g.id ?? (!baseLabels.has(g.label) && items.length > 0
      ? legacyId(g, groupPosition)
      : undefined)
    // An unknown-only old section is not a recovery route and cannot grant a
    // destination. A non-base id-less section with valid routes is historical
    // authored grouping, however, and is upgraded to stable identity instead
    // of being mistaken for a renamed Console section.
    if (!id && !baseLabels.has(g.label)) continue
    rows.push({ ...(id ? { id } : {}), label: g.label, hidden: Boolean(g.hidden), items })
  }

  const byBaseLabel = new Map(rows.filter((r) => !r.id).map((r) => [r.label, r]))
  for (const b of base.groups) {
    if (byBaseLabel.has(b.label)) continue
    const row: NavEditGroup = { label: b.label, hidden: false, items: [] }
    byBaseLabel.set(b.label, row)
    rows.push(row)
  }
  for (const b of base.groups) {
    for (const i of b.items ?? []) {
      if (placed.has(i.to)) continue
      placed.add(i.to)
      byBaseLabel.get(declaredIn.get(i.to)!)!.items.push({
        label: i.label, to: i.to, hidden: false, ...(i.iconKey ? { iconKey: i.iconKey } : {}),
        ...(i.pageKey ? { pageKey: i.pageKey } : {}),
      })
    }
  }
  return rows
}

/** The document to store. Labels come from the rows, which came from the base. */
export function documentFrom(rows: NavEditGroup[]): NavDocument {
  if (rows.some((group) => group.recovery && group.items.length > 0)) {
    throw new Error('Move every unparented section into a destination before saving.')
  }
  return {
    groups: rows.filter((group) => !group.recovery).map((g) => ({
      // Without this the identity of a created section lives exactly as long as
      // the sheet is open: reopen the editor and two sections called "Mine" are
      // one, and neither is renameable.
      ...(g.id ? { id: g.id } : {}),
      label: g.label,
      ...(g.hidden ? { hidden: true } : {}),
      items: g.items.map((i) => ({
        label: i.label, to: i.to,
        ...(i.iconKey ? { iconKey: i.iconKey } : {}),
        ...(i.pageKey ? { pageKey: i.pageKey } : {}),
        ...(i.hidden ? { hidden: true } : {}),
      })),
    })),
  }
}

/** Compatibility export; Experience owns the item-move algorithm. */
export function moved<T>(list: T[], from: number, to: number): T[] {
  return movedByIndex(list, from, to)
}

/**
 * THE item move, whichever section it lands in.
 *
 * `moved()` alone could not express a cross-group drop — it reorders one list —
 * and the editor's parity claim rests on there being ONE reducer behind the
 * pointer route, the grab-mode route and the Move buttons. So the same-group
 * case still delegates to `moved()` and the cross-group case is the only new
 * arithmetic in the file. Both routes call this; neither splices for itself.
 *
 * A cross-group insert may target `dst.items.length` (append past the end) —
 * that is how the keyboard route enters a section from above it, and how an
 * empty invented section can receive its first link at all.
 */
export function moveItem(
  rows: NavEditGroup[],
  from: { gi: number; index: number },
  to: { gi: number; index: number },
): NavEditGroup[] {
  const src = rows[from.gi]
  const dst = rows[to.gi]
  if (!src || !dst) return rows
  const item = src.items[from.index]
  if (!item) return rows

  if (from.gi === to.gi) {
    const items = moved(src.items, from.index, to.index)
    if (items === src.items) return rows
    return rows.map((g, i) => (i === from.gi ? { ...g, items } : g))
  }
  if (to.index < 0 || to.index > dst.items.length) return rows
  const out = src.items.filter((_, j) => j !== from.index)
  const into = [...dst.items.slice(0, to.index), item, ...dst.items.slice(to.index)]
  return rows.map((g, i) => (
    i === from.gi ? { ...g, items: out } : i === to.gi ? { ...g, items: into } : g))
}

/* ──────────────────────────────────────────────────────────────────────────
 * THE LADDER — the fourth question, and the one nobody could answer (#1466 B3)
 *
 * The three questions above are all about ONE document. The bar a person is
 * looking at is not one document: it is `platform recipe → deployment default →
 * their role's default → their own pin`, resolved by `core.navigation()`, which
 * "deliberately answers what the bar IS, not which layer it came from"
 * (`2026081012:240`). Measured consequence, and it is the whole of this section:
 * the tree shipped THREE-layer language over TWO rungs of data that were never
 * shown to anyone (comment 47068 §3), so "why is this link here" and "who will
 * my change affect" had no answer on any screen.
 *
 * Two rules live here rather than in the editor, because both hosts need them
 * and `packages/ui` may fetch nothing (M3):
 *
 *   `presentRungs`  — WHICH rungs to draw. Owner ruling M2: the ones that
 *                     actually have a row, named for what they are. A fixed
 *                     three would be a lie on any deployment carrying a
 *                     deployment-wide override row, and equally a lie on the
 *                     ordinary box that has none.
 *   `layerBelow`    — WHAT a pin reconciles against. Owner ruling O3.
 *
 * ── O3, said plainly, because it HIDES things people can see today ─────────
 * `reconcile` appends every base route the pin does not mention. Called with
 * the platform recipe — which is what both hosts did until this change
 * (`Nav.tsx:257`, `portalNav.ts:88`) — a route a persona default deliberately
 * EXCLUDED reappears in a pinned user's bar the moment it enters the recipe.
 * Not a privilege leak (the recipe is still gated by `isInstalled`/`showBackstage`
 * in the console and by `exists.has(i.to)` in the portal, and a nav item is a
 * courtesy rather than an authority boundary — ADR F-39), but a CURATION leak:
 * the administrator's list is quietly overridden by the act of having once
 * reordered your own sidebar.
 *
 * Ruled (comment 47222 §3): a pin reconciles against the effective layer
 * immediately BELOW it. So on a deployment whose persona default curates six
 * links out of thirty-seven, a pinned user stops being appended the other
 * thirty-one. **That is a visible behaviour change on live boxes and it removes
 * links from bars that show them today.** The way back is unchanged and is one
 * click: Reset drops the pin, and the persona default renders as authored.
 */

/**
 * One rung of the cascade, as the HOST sees it. `document: null` means the rung
 * exists in the model and has no row on this deployment — which is the normal
 * state of three of the four rungs, and the reason `presentRungs` exists.
 */
export interface NavLayer {
  /** Stable, host-chosen: `platform` | `deployment` | `persona` | `user`, or a rung a host invents. */
  key: string
  /** What this rung is CALLED to the person reading it — "Your role", not `persona`. */
  label: string
  /** The document this rung contributes, or null when it has no row. */
  document: NavDocument | null
  /** True on the ONE rung this editor writes. Drawn even with no row: it is where the edit lands. */
  editing?: boolean
}

/**
 * The rungs worth drawing: every rung that has a real document, plus the rung
 * being edited — which is drawn even when it is empty, because "where will this
 * land" is the other half of the question the strip answers, and the first pin
 * anybody makes is written to a rung that had no row a moment earlier.
 *
 * `isNavDocument` rather than truthiness, for the reason its own header gives:
 * these arrive from jsonb RPCs and `[]` is not a bar.
 */
export function presentRungs(layers: NavLayer[] | null | undefined): NavLayer[] {
  if (!Array.isArray(layers)) return []
  return layers.filter((l) => l && (l.editing === true || isNavDocument(l.document)))
}

/**
 * The effective layer immediately BELOW the one being edited — O3's whole rule,
 * in one place so the two hosts cannot answer it differently.
 *
 * "Immediately below" is by PRESENCE, not by position: the ladder is ordered
 * platform-first, and the answer is the last rung before the edited one that
 * actually has a row. A deployment with no persona default therefore reconciles
 * a pin against the deployment default, and one with neither against the
 * platform recipe — which is exactly the behaviour that shipped, now reached as
 * a case rather than as the only case.
 *
 * Returns null when no rung below has a row, so the caller supplies its own
 * base explicitly rather than being handed an empty bar that would delete every
 * link in the editor. `packages/ui` knows no base (M3).
 */
export function layerBelow(layers: NavLayer[] | null | undefined): NavDocument | null {
  if (!Array.isArray(layers)) return null
  const upto = layers.findIndex((l) => l?.editing === true)
  const below = upto < 0 ? layers : layers.slice(0, upto)
  for (let i = below.length - 1; i >= 0; i--) {
    const doc = below[i]?.document
    if (isNavDocument(doc) && doc.groups.length > 0) return doc
  }
  return null
}

/** Which rung decided a thing. Keyed by route for links, by `id ?? label` for sections. */
export interface NavAttribution {
  items: Map<string, string>
  groups: Map<string, string>
}

/**
 * WHERE each row came from — the sentence "this bar is platform → your role →
 * yours", drawn per row instead of asserted in prose.
 *
 * A link is attributed to the rung that last DECIDED something about it: the
 * section it sits in, or whether it is hidden. **Order is deliberately not
 * attributed.** An index shifts when anything above it moves, so attributing
 * position would name a rung on every row below the one real edit — a badge
 * that says "You" beside a link you never touched is worse than no badge,
 * because it is confidently wrong.
 *
 * A section is attributed to the rung that INTRODUCED it, which is the whole
 * question for a section: a base section is the platform's, an invented one is
 * whoever invented it, and a base section cannot be renamed away (`rowsFor`
 * round-trips the platform's word for it).
 *
 * Walks the ladder bottom-up — platform first — so a later rung overwrites an
 * earlier one only where it disagrees.
 */
export function attribution(layers: NavLayer[] | null | undefined): NavAttribution {
  const items = new Map<string, string>()
  const groups = new Map<string, string>()
  if (!Array.isArray(layers)) return { items, groups }
  const placed = new Map<string, { group: string; hidden: boolean }>()
  for (const layer of layers) {
    if (!layer || !isNavDocument(layer.document)) continue
    for (const g of layer.document.groups) {
      if (!g) continue
      const gkey = g.id ?? g.label
      if (!groups.has(gkey)) groups.set(gkey, layer.key)
      for (const i of g.items ?? []) {
        if (!i) continue
        const now = { group: g.label, hidden: Boolean(i.hidden) }
        const was = placed.get(i.to)
        if (was && was.group === now.group && was.hidden === now.hidden) continue
        placed.set(i.to, now)
        items.set(i.to, layer.key)
      }
    }
  }
  return { items, groups }
}
